/**
 * Admin → Test Company impersonation settings page.
 *
 * One-stop UI for the iter112 feature:
 *   1. Search the customer table → pick a single company as your
 *      "test sandbox dealer".
 *   2. While a target is set but impersonation is OFF, that company's
 *      compressors are hidden from the regular Products Sold list.
 *   3. Flip the toggle to ON and the entire app starts behaving as if
 *      that company were logged in (compressor list filtered to their
 *      fleet only, etc.). A persistent amber banner appears at the
 *      top of every page until you Exit.
 *
 * Restricted to manufacturer staff (@usaircompressor.com /
 * @aircompressorcanada.com). Customer-role users hitting this URL
 * get a 403 from the backend.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { TestTube, MagnifyingGlass, Spinner, Buildings, CheckCircle, X, Warning } from "@phosphor-icons/react";
import AdminPageHeader from "@/components/AdminPageHeader";
import { useT } from "@/lib/i18n";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function TestCompanyAdminPage({ user, onLogout, onLogoutAll }) {
  const t = useT();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // Search bar (customer lookup)
  const [searchQ, setSearchQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const refresh = async () => {
    setLoading(true); setErr("");
    try {
      const r = await axios.get(`${API}/admin/test-company`, {
        withCredentials: true, headers: { "X-User-Email": user.email },
      });
      setState(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  // Debounced customer search
  useEffect(() => {
    if (!searchQ || searchQ.length < 2) { setResults([]); return; }
    const id = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await axios.get(`${API}/customers`, {
          params: { country_code: "ALL", search: searchQ, per_page: 12 },
          withCredentials: true,
          headers: { "X-User-Email": user.email },
        });
        setResults(r.data?.items || []);
      } catch {
        setResults([]);
      } finally { setSearching(false); }
    }, 280);
    return () => clearTimeout(id);
  }, [searchQ, user.email]);

  const setTarget = async (cid) => {
    try {
      await axios.put(`${API}/admin/test-company`,
        { customer_id: cid },
        { withCredentials: true, headers: { "X-User-Email": user.email } });
      toast.success("Test company designated");
      setSearchQ(""); setResults([]);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to set");
    }
  };

  const clearTarget = async () => {
    try {
      await axios.put(`${API}/admin/test-company`,
        { customer_id: "" },
        { withCredentials: true, headers: { "X-User-Email": user.email } });
      toast.success("Test company cleared");
      refresh();
    } catch {
      toast.error("Failed to clear");
    }
  };

  const setMode = async (impersonating) => {
    try {
      await axios.put(`${API}/admin/test-company/mode`,
        { impersonating },
        { withCredentials: true, headers: { "X-User-Email": user.email } });
      toast.success(impersonating ? "Impersonation ON — page will reload" : "Impersonation OFF");
      // Hard reload so banner + all list state refreshes.
      setTimeout(() => window.location.reload(), 600);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to toggle");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 font-['Lato'] text-slate-900" data-testid="test-company-page">
      <AdminPageHeader
        sticky
        maxWidth="max-w-3xl"
        testId="test-company-back"
        icon={<TestTube size={20} weight="fill" className="text-amber-700" />}
        title={t("test_company.title", "Test Company")}
        subtitle={t("test_company.subtitle", "Designate a sandbox dealer + impersonate to QA the dealer experience.")}
        user={user}
        onLogout={onLogout}
        onLogoutAll={onLogoutAll}
      />

      <main className="max-w-3xl mx-auto p-4 md:p-6 space-y-5">
        {loading && (
          <div className="text-xs text-slate-500 inline-flex items-center gap-2">
            <Spinner size={12} className="animate-spin" /> {t("common.loading", "Loading…")}
          </div>
        )}
        {err && (
          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-sm p-2">{err}</div>
        )}

        {!loading && !err && (
          <>
            {/* Current state card */}
            <section className="bg-white rounded-md border border-slate-200 overflow-hidden">
              <header className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
                <Buildings size={16} weight="fill" className="text-slate-500" />
                <h2 className="text-sm font-bold font-['Chivo']">{t("test_company.current_label", "Designated test company")}</h2>
              </header>
              <div className="p-4">
                {state.customer_id ? (
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <div className="text-base font-semibold text-slate-900" data-testid="test-company-current-name">
                        {state.customer_label || state.customer_id}
                      </div>
                      <div className="text-[11px] text-slate-500 font-mono mt-0.5">CustomerID: {state.customer_id}</div>
                      <div className="text-[11px] text-slate-500 mt-1">
                        {state.impersonating
                          ? t("test_company.status_active", "Currently viewing the app AS this company.")
                          : t("test_company.status_hidden", "Their compressors are hidden from your normal Products Sold view.")}
                      </div>
                    </div>
                    <button onClick={clearTarget}
                      data-testid="test-company-clear"
                      className="text-[11px] font-semibold uppercase tracking-wider px-3 h-8 rounded-sm border border-slate-200 hover:border-red-300 hover:text-red-700 inline-flex items-center gap-1.5">
                      <X size={11} weight="bold" /> {t("test_company.clear_btn", "Clear")}
                    </button>
                  </div>
                ) : (
                  <p className="text-xs text-slate-500 italic">{t("test_company.none_selected", "No test company picked yet — search below.")}</p>
                )}
              </div>

              {state.customer_id && (
                <div className={`px-4 py-3 border-t flex items-center justify-between gap-3 ${state.impersonating ? "bg-amber-50 border-amber-200" : "bg-slate-50 border-slate-100"}`}>
                  <div className="flex items-center gap-2 text-xs">
                    {state.impersonating ? (
                      <Warning size={14} weight="fill" className="text-amber-700" />
                    ) : (
                      <CheckCircle size={14} weight="fill" className="text-emerald-600" />
                    )}
                    <span className={state.impersonating ? "text-amber-900 font-semibold" : "text-slate-600"}>
                      {state.impersonating
                        ? t("test_company.toggle_on_label", "Impersonation is ON")
                        : t("test_company.toggle_off_label", "Impersonation is OFF")}
                    </span>
                  </div>
                  <button
                    onClick={() => setMode(!state.impersonating)}
                    data-testid={`test-company-toggle-${state.impersonating ? "off" : "on"}`}
                    className={`text-[11px] font-bold uppercase tracking-wider px-3 h-8 rounded-sm transition inline-flex items-center gap-1.5 ${state.impersonating ? "bg-amber-700 text-white hover:bg-amber-800" : "bg-blue-700 text-white hover:bg-blue-800"}`}>
                    {state.impersonating
                      ? t("test_company.exit_btn", "Exit impersonation")
                      : t("test_company.enter_btn", "Enter impersonation")}
                  </button>
                </div>
              )}
            </section>

            {/* Search to set / change */}
            <section className="bg-white rounded-md border border-slate-200 overflow-hidden">
              <header className="px-4 py-3 border-b border-slate-100">
                <h2 className="text-sm font-bold font-['Chivo']">
                  {state.customer_id
                    ? t("test_company.change_label", "Change test company")
                    : t("test_company.pick_label", "Pick a test company")}
                </h2>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  {t("test_company.search_hint", "Search by company name or ID.")}
                </p>
              </header>
              <div className="p-4 space-y-3">
                <label className="relative block">
                  <MagnifyingGlass size={12} className="absolute left-2.5 top-2.5 text-slate-400" />
                  <input
                    type="text"
                    value={searchQ}
                    onChange={(e) => setSearchQ(e.target.value)}
                    data-testid="test-company-search"
                    placeholder={t("test_company.search_placeholder", "e.g. Acme Industries")}
                    className="w-full text-xs pl-7 pr-3 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </label>
                {searching && (
                  <div className="text-[11px] text-slate-500 inline-flex items-center gap-1.5"><Spinner size={11} className="animate-spin" /> Searching…</div>
                )}
                {results.length > 0 && (
                  <ul className="border border-slate-200 rounded-sm divide-y divide-slate-100 max-h-72 overflow-y-auto">
                    {results.map((c) => (
                      <li key={c.CustomerID}
                        className="flex items-center gap-2 px-2.5 py-1.5 text-xs hover:bg-blue-50/40 cursor-pointer"
                        onClick={() => setTarget(c.CustomerID)}
                        data-testid={`test-company-result-${c.CustomerID}`}>
                        <Buildings size={12} className="text-slate-400 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-slate-800 truncate">{c["Company Name"] || "—"}</div>
                          <div className="text-[10px] text-slate-500 truncate">
                            {c.CustomerID} · {c.Type || "—"}
                          </div>
                        </div>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-blue-700 shrink-0">Pick →</span>
                      </li>
                    ))}
                  </ul>
                )}
                {!searching && searchQ.length >= 2 && results.length === 0 && (
                  <p className="text-xs text-slate-500 italic">No matches.</p>
                )}
              </div>
            </section>

            {/* Educational footer */}
            <section className="text-[11px] text-slate-500 leading-relaxed bg-amber-50 border border-amber-200 rounded-sm p-3">
              <p className="font-semibold text-amber-900 mb-1">How it works</p>
              <ul className="list-disc list-inside space-y-0.5 text-amber-900/80">
                <li>The "designated" company's compressors are hidden from your normal Products Sold list, so test data doesn't pollute reporting.</li>
                <li>Toggling "Enter impersonation" makes every list filter as if that dealer were logged in — useful for QAing the dealer experience without sharing credentials.</li>
                <li>Each manufacturer-staff admin keeps their own designation independently.</li>
                <li>An amber banner appears at the top of every page while impersonation is active.</li>
              </ul>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
